import { Component, input, output, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { trigger, transition, style, animate } from '@angular/animations';

export interface ToastConfig {
  id?: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'cart';
  title: string;
  message?: string;
  duration?: number;
  showClose?: boolean;
  productImage?: string;
  productName?: string;
  productPrice?: number;
  actionLabel?: string;
  actionCallback?: () => void;
}

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CurrencyPipe, MatIconModule, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('toastAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(100%)', opacity: 0 }))
      ])
    ])
  ],
  template: `
    <div
      class="toast"
      [class.toast-success]="config().type === 'success'"
      [class.toast-error]="config().type === 'error'"
      [class.toast-warning]="config().type === 'warning'"
      [class.toast-info]="config().type === 'info'"
      [class.toast-cart]="config().type === 'cart'"
      [@toastAnimation]
      (click)="onToastClick()">

      <!-- Icon -->
      <div class="toast-icon">
        @if (config().type === 'success') { <mat-icon>check_circle</mat-icon> }
        @if (config().type === 'error') { <mat-icon>error</mat-icon> }
        @if (config().type === 'warning') { <mat-icon>warning</mat-icon> }
        @if (config().type === 'info') { <mat-icon>info</mat-icon> }
        @if (config().type === 'cart') { <mat-icon>shopping_cart</mat-icon> }
      </div>

      <!-- Content -->
      <div class="toast-content">
        <!-- Cart toast with product image -->
        @if (config().type === 'cart' && config().productImage) {
          <div class="toast-product">
            <img [src]="config().productImage" [alt]="config().productName" class="product-thumb" loading="lazy">
            <div class="product-info">
              <span class="toast-title">{{ config().title }}</span>
              <span class="product-name">{{ config().productName }}</span>
              @if (config().productPrice) {
                <span class="product-price">
                  {{ config().productPrice | currency:'ARS':'symbol':'1.2-2' }}
                </span>
              }
            </div>
          </div>
        }

        <!-- Standard toast -->
        @if (config().type !== 'cart' || !config().productImage) {
          <span class="toast-title">{{ config().title }}</span>
          @if (config().message) {
            <span class="toast-message">{{ config().message }}</span>
          }
        }
      </div>

      <!-- Action button -->
      @if (config().actionLabel) {
        <button
          class="toast-action"
          (click)="onActionClick($event)">
          {{ config().actionLabel }}
        </button>
      }

      <!-- Close button -->
      @if (config().showClose) {
        <button
          class="toast-close"
          (click)="onClose($event)">
          <mat-icon>close</mat-icon>
        </button>
      }

      <!-- Progress bar -->
      @if (showProgress) {
      <div class="toast-progress">
        <div class="progress-bar" [style.animation-duration.ms]="config().duration"></div>
      </div>
      }
    </div>
  `,
  styles: [`
    .toast {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px;
      background: #141414;
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
      min-width: 300px;
      max-width: 420px;
      position: relative;
      overflow: hidden;
      cursor: pointer;
      border-left: 4px solid;
    }

    .toast-success {
      border-left-color: #22c55e;
      .toast-icon mat-icon { color: #22c55e; }
    }

    .toast-error {
      border-left-color: #ef4444;
      .toast-icon mat-icon { color: #ef4444; }
    }

    .toast-warning {
      border-left-color: #f59e0b;
      .toast-icon mat-icon { color: #f59e0b; }
    }

    .toast-info {
      border-left-color: #888;
      .toast-icon mat-icon { color: #888; }
    }

    .toast-cart {
      border-left-color: var(--color-primary, #888);
      .toast-icon mat-icon { color: var(--color-primary, #888); }
    }

    .toast-icon {
      flex-shrink: 0;
      mat-icon {
        font-size: 24px;
        width: 24px;
        height: 24px;
      }
    }

    .toast-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .toast-title {
      font-weight: 600;
      color: #f5f5f5;
      font-size: 14px;
    }

    .toast-message {
      color: rgba(255, 255, 255, 0.7);
      font-size: 13px;
      line-height: 1.4;
    }

    .toast-product {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .product-thumb {
      width: 48px;
      height: 48px;
      border-radius: 8px;
      object-fit: cover;
      flex-shrink: 0;
    }

    .product-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .product-name {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.7);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .product-price {
      font-size: 14px;
      font-weight: 600;
      color: var(--color-primary, #888);
    }

    .toast-action {
      background: none;
      border: none;
      color: var(--color-primary, #888);
      font-weight: 600;
      font-size: 13px;
      cursor: pointer;
      padding: 6px 12px;
      border-radius: 6px;
      transition: background 0.2s;
      white-space: nowrap;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }

    .toast-close {
      background: none;
      border: none;
      padding: 4px;
      cursor: pointer;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: rgba(255, 255, 255, 0.5);
      transition: all 0.2s;
      flex-shrink: 0;

      &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: rgba(255, 255, 255, 0.7);
      }

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }

    .toast-progress {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: rgba(255, 255, 255, 0.05);
    }

    .progress-bar {
      height: 100%;
      background: currentColor;
      opacity: 0.3;
      animation: progress-shrink linear forwards;
    }

    @keyframes progress-shrink {
      from { width: 100%; }
      to { width: 0%; }
    }

    @media (max-width: 480px) {
      .toast {
        min-width: unset;
        max-width: calc(100vw - 32px);
      }
    }
  `]
})
export class ToastComponent implements OnInit, OnDestroy {
  readonly config = input.required<ToastConfig>();
  readonly closed = output<void>();

  showProgress = true;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  ngOnInit() {
    const cfg = this.config();
    if (cfg.duration && cfg.duration > 0) {
      this.timeoutId = setTimeout(() => {
        this.closed.emit();
      }, cfg.duration);
    } else {
      this.showProgress = false;
    }
  }

  ngOnDestroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  onToastClick() {
    if (this.config().actionCallback) {
      this.config().actionCallback!();
    }
  }

  onActionClick(event: Event) {
    event.stopPropagation();
    if (this.config().actionCallback) {
      this.config().actionCallback!();
    }
    this.closed.emit();
  }

  onClose(event: Event) {
    event.stopPropagation();
    this.closed.emit();
  }
}
